import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/lib/authContext";
import { api, PayPeriod } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format, parseISO, addDays } from "date-fns";
import { Loader2, Calendar, Plus, Lock, Unlock, AlertCircle, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";

export default function PayPeriodAdmin() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  
  const isAdmin = user?.role === "admin";
  const [processing, setProcessing] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<{ id: string; action: "close" | "reopen" } | null>(null);
  
  const [newPeriod, setNewPeriod] = useState({
    start_date: "",
    end_date: "",
    pay_date: "",
  });

  useEffect(() => {
    loadPeriods();
  }, []);

  const loadPeriods = async () => {
    try {
      const data = await api.getPayPeriods();
      setPeriods(data);
    } catch (error) {
      console.error("Failed to load pay periods:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newPeriod.start_date || !newPeriod.end_date || !newPeriod.pay_date) {
      toast({
        title: "Missing Information",
        description: "Please fill in all date fields.",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);
    try {
      await api.createPayPeriod(newPeriod);
      toast({
        title: "Pay Period Created",
        description: "The new pay period has been created successfully.",
      });
      setShowCreateDialog(false);
      setNewPeriod({ start_date: "", end_date: "", pay_date: "" });
      await loadPeriods();
    } catch (error: any) {
      toast({
        title: "Creation Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = async (id: string) => {
    setProcessing(true);
    try {
      await api.closePayPeriod(id);
      toast({
        title: "Pay Period Closed",
        description: "The pay period has been closed. Time entries can no longer be edited.",
      });
      setShowConfirmDialog(null);
      await loadPeriods();
    } catch (error: any) {
      toast({
        title: "Close Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReopen = async (id: string) => {
    setProcessing(true);
    try {
      await api.reopenPayPeriod(id);
      toast({
        title: "Pay Period Reopened",
        description: "The pay period is now open for editing.",
      });
      setShowConfirmDialog(null);
      await loadPeriods();
    } catch (error: any) {
      toast({
        title: "Reopen Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const suggestNextPeriod = () => {
    if (periods.length === 0) {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 15);
      const pay = addDays(end, 5);
      setNewPeriod({
        start_date: format(start, "yyyy-MM-dd"),
        end_date: format(end, "yyyy-MM-dd"),
        pay_date: format(pay, "yyyy-MM-dd"),
      });
    } else {
      const lastPeriod = periods[0];
      const lastEnd = parseISO(lastPeriod.end_date);
      const newStart = addDays(lastEnd, 1);
      const newEnd = addDays(newStart, 13);
      const newPay = addDays(newEnd, 5);
      setNewPeriod({
        start_date: format(newStart, "yyyy-MM-dd"),
        end_date: format(newEnd, "yyyy-MM-dd"),
        pay_date: format(newPay, "yyyy-MM-dd"),
      });
    }
    setShowCreateDialog(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-3xl font-heading font-bold tracking-tight" data-testid="text-page-title">Pay Period Administration</h1>
          <Card className="border-dashed border-2 border-destructive/50">
            <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ShieldAlert className="w-12 h-12 mb-4 text-destructive/50" />
              <p className="text-lg font-medium">Access Denied</p>
              <p className="text-sm">You need administrator privileges to manage pay periods.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const openPeriods = periods.filter(p => p.status === "open");
  const closedPeriods = periods.filter(p => p.status === "closed");

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight" data-testid="text-page-title">Pay Period Administration</h1>
            <p className="text-muted-foreground">Manage pay periods for time tracking and payroll.</p>
          </div>
          
          <Button onClick={suggestNextPeriod} data-testid="button-create-period">
            <Plus className="w-4 h-4 mr-2" />
            Create Pay Period
          </Button>
        </div>

        {periods.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Calendar className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No Pay Periods</p>
              <p className="text-sm mb-4">Create your first pay period to get started.</p>
              <Button onClick={suggestNextPeriod}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Period
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {openPeriods.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <Unlock className="w-5 h-5 text-green-600" />
                  Open Periods ({openPeriods.length})
                </h2>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Period</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Pay Date</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Status</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {openPeriods.map(period => (
                        <tr key={period.id} className="hover:bg-muted/20" data-testid={`card-period-${period.id}`}>
                          <td className="px-4 py-2.5 font-medium">
                            {format(parseISO(period.start_date), "MMM d")} – {format(parseISO(period.end_date), "MMM d, yyyy")}
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">
                            {format(parseISO(period.pay_date), "MMM d, yyyy")}
                          </td>
                          <td className="px-4 py-2.5">
                            <Badge variant="default" className="bg-green-600">Open</Badge>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2.5 text-xs"
                              onClick={() => setShowConfirmDialog({ id: period.id, action: "close" })}
                              data-testid={`button-close-${period.id}`}
                            >
                              <Lock className="w-3.5 h-3.5 mr-1.5" />
                              Close
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {closedPeriods.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-muted-foreground" />
                  Closed Periods ({closedPeriods.length})
                </h2>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Period</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Pay Date</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Closed</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {closedPeriods.map(period => (
                        <tr key={period.id} className="hover:bg-muted/20 bg-muted/10" data-testid={`card-period-${period.id}`}>
                          <td className="px-4 py-2.5 text-muted-foreground">
                            {format(parseISO(period.start_date), "MMM d")} – {format(parseISO(period.end_date), "MMM d, yyyy")}
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">
                            {format(parseISO(period.pay_date), "MMM d, yyyy")}
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground text-xs">
                            {period.closed_at ? format(parseISO(period.closed_at), "MMM d, yyyy") : "—"}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2.5 text-xs"
                              onClick={() => setShowConfirmDialog({ id: period.id, action: "reopen" })}
                              data-testid={`button-reopen-${period.id}`}
                            >
                              <Unlock className="w-3.5 h-3.5 mr-1.5" />
                              Reopen
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Pay Period</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={newPeriod.start_date}
                  onChange={(e) => setNewPeriod({ ...newPeriod, start_date: e.target.value })}
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={newPeriod.end_date}
                  onChange={(e) => setNewPeriod({ ...newPeriod, end_date: e.target.value })}
                  data-testid="input-end-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pay_date">Pay Date</Label>
                <Input
                  id="pay_date"
                  type="date"
                  value={newPeriod.pay_date}
                  onChange={(e) => setNewPeriod({ ...newPeriod, pay_date: e.target.value })}
                  data-testid="input-pay-date"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={processing} data-testid="button-confirm-create">
                {processing ? "Creating..." : "Create Period"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!showConfirmDialog} onOpenChange={() => setShowConfirmDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                {showConfirmDialog?.action === "close" ? "Close Pay Period?" : "Reopen Pay Period?"}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {showConfirmDialog?.action === "close" ? (
                <p className="text-muted-foreground">
                  Closing this pay period will prevent any further edits to time entries within this period.
                  This action can be reversed by reopening the period later.
                </p>
              ) : (
                <p className="text-muted-foreground">
                  Reopening this pay period will allow users to edit their time entries again.
                  Make sure this is intended before proceeding.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmDialog(null)}>Cancel</Button>
              <Button
                variant={showConfirmDialog?.action === "close" ? "default" : "outline"}
                onClick={() => {
                  if (showConfirmDialog?.action === "close") {
                    handleClose(showConfirmDialog.id);
                  } else if (showConfirmDialog?.action === "reopen") {
                    handleReopen(showConfirmDialog.id);
                  }
                }}
                disabled={processing}
                data-testid="button-confirm-action"
              >
                {processing ? "Processing..." : showConfirmDialog?.action === "close" ? "Close Period" : "Reopen Period"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
